/**
 * @module TeamManageApi
 * @description 团队管理相关 API 接口
 *
 * 本模块提供团队管理的完整后端接口封装，包括：
 * - 团队的创建、查询、更新、删除
 * - 成员的邀请、查询、更新、删除
 * - 积分相关配置
 */
import { EnumPointShareType, EnumMemberStatus, EnumTeamStatus } from "@/types/enum";
import { http } from "@/api/request";

import { useUserStore } from "@/store/modules/user";

/** 创建团队 - 请求参数 */
interface CreateTeamRequest {
  /** 团队名称（必填，最大15字符） */
  name: string;
  /** 团队描述（可选，最大25字符） */
  description?: string;
  /** 每月积分上限（必填，正整数） */
  monthly_points_limit: number;
  /**
   * 积分处理方式（必填）
   * - 1: 个人积分全部保留
   * - 2: 个人积分全部转让至团队账户
   */
  points_processing: number;
}

/** 创建团队 - 响应数据 */
interface CreateTeamResponse {
  /** 团队 ID */
  team_id: string;
  /** 团队名称 */
  name: string;
  /** 团队描述 */
  description: string;
  /** 每月积分上限 */
  monthly_points_limit: number;
  /** 积分处理方式 */
  points_processing: number;
  /** 创建时间 */
  created_at: string;
}

/** 获取用户已加入团队列表 - 响应项 */
export interface TeamListItemResponse {
  /** 团队 ID */
  team_id: string;
  /** 团队名称 */
  name: string;
  /** 团队描述 */
  description: string;
  /** 团队状态（1: 生效中, 2: 已停用） */
  status: EnumTeamStatus;
  /** 成员数量 */
  member_count: number;
  /** 当前用户在该团队下的角色（1: 团队管理员, 2: 普通成员） */
  role: number;
  /** 团队积分余额 */
  points_balance: number;
  /** 本月已用积分 */
  used_points_monthly: number;
  /** 团队每月积分上限 */
  monthly_points_limit: number;
}

/** 获取团队详情 - 响应数据 */
export interface GetTeamDetailResponse {
  /** 团队 ID */
  team_id: string;
  /** 团队名称 */
  name: string;
  /** 团队描述 */
  description: string;
  /** 团队状态（1: 生效中, 2: 已停用） */
  status: number;
  /** 成员数量 */
  member_count: number;
  /** 团队累计已使用积分 */
  used_points: number;
  /** 团队每月积分上限 */
  monthly_points_limit: number;
  /** 团队积分余额 */
  points_balance: number;
  /** 团队自创建起累计购买积分数 */
  total_points_purchased: number;
  /** 本月已用积分 */
  used_points_monthly: number;
  /** 当前用户在该团队下的角色（1: 团队管理员, 2: 普通成员） */
  role: number;
  /** 当前用户在该团队下有哪些页面权限 */
  perms: string[];
  /** 每月度预警提醒 */
  monthly_warning_reminder: boolean;
  /** 月度积分消耗预警提醒阈值 */
  monthly_warning_threshold?: number | string;
  /** 团队剩余积分预警提醒 */
  balance_warning_reminder: boolean;
  /** 团队剩余积分预警提醒阈值 */
  balance_warning_threshold?: number | string;
  /** 成员是否可见团队积分余额（1: 可见, 0: 不可见） */
  point_balance_visible: number;
  /** 团队已购套餐会员 */
  team_package_info: {
    member_package: MemberPlan | null;
    old_member_packages: MemberPlan[];
    purchased_points: number;
  };
}

/** 团队成员项 */
export interface MemberItem {
  /** 成员记录 ID, 实际是 member_record_id */
  id: number;
  /** 用户 ID */
  user_id: string;
  /** 团队 ID */
  team_id: string;
  /** 成员 ID */
  member_id: string;
  /** 姓名备注 */
  name: string;
  /** 职位 */
  position: string;
  /** 手机号 */
  phone: string;
  /** 积分分配方式（1: 共享团队积分, 2: 设置积分上限） */
  point_share_type: EnumPointShareType;
  /** 积分上限（仅 point_share_type=2 时有值） */
  point_limit?: number;
  /** 成员是否可见团队积分余额（1: 可见, 0: 不可见） */
  point_balance_visible: number;
  /** 已用积分 */
  used_points: number;
  /** 生成记录数 */
  generated_records: number;
  /** 状态（0: 待加入, 1: 已加入, 2: 已停用） */
  status: EnumMemberStatus;
  /** 添加时间 */
  created_at?: string;
  /** 成员已有的权限列表 */
  perms?: string[];
  /** 成员角色（1: 团队管理员, 2: 普通成员） */
  role: number;
}

/** 获取团队成员列表 - 请求参数 */
interface GetMemberListRequest {
  /** 团队 ID（必填） */
  team_id: string;
  /** 搜索关键词（姓名或手机号或邮箱地址） */
  keyword?: string;
  /** 状态 */
  status?: EnumMemberStatus;
  /** 开始时间 */
  start_date?: string;
  /** 结束时间 */
  end_date?: string;
  /** 页码（默认1） */
  page?: number;
  /** 每页数量（默认20） */
  page_size?: number;
}

/** 更新团队 - 请求参数 */
interface UpdateTeamRequest {
  /** 团队id */
  team_id: string;
  /** 团队名称（必填，最大15字符） */
  name: string;
  /** 团队描述（可选，最大25字符） */
  description?: string;
  /** 团队每月积分上限（必填，正整数，不能低于已用积分） */
  monthly_points_limit: number;
}

/** 转让团队 - 请求参数 */
export interface TransferTeamRequest {
  /** 转让的团队Id */
  team_id: string;
  /** 接收转让的成员 ID（必填） */
  member_record_id: string;
  /**
   * 积分处理方式（必填）
   * - 1: 积分同步转让（保留在团队账户）
   * - 2: 积分转移至个人账户（团队账户清零）
   */
  points_processing: number;
}

/** 邀请成员 - 请求参数 */
interface InviteMemberRequest {
  /** 团队 ID（必填） */
  team_id: string;
  /** 姓名备注（必填，**不可重复**） */
  name: string;
  /** 职位（必填） */
  position: string;
  /**
   * 积分分配方式（必填）
   * - 1: 共享团队积分
   * - 2: 单独设置积分使用额度
   */
  point_share_type: number;
  /** 成员是否可见积分余额（仅 point_share_type=1 时可为1） */
  point_balance_visible?: number;
  /** 积分上限（仅 point_share_type=2 时必填，最大10000） */
  limit?: number;
  /** 开通的权限列表 */
  perms?: string[];
}

/** 邀请成员 - 响应数据 */
interface InviteMemberResponse {
  /** 邀请id，可用一次，用过一次自动作废 */
  invite_id: string;
  /** 链接过期时间 */
  expire_time: string;
}

/** 重新邀请成员 - 请求参数 */
interface RedoMemberInviteRequest {
  /** 团队 ID（必填） */
  team_id: string;
  /** 成员记录 ID */
  member_record_id: string;
}

/** 更新成员信息 - 请求参数 */
interface UpdateMemberInfoRequest {
  /** 姓名备注（必填，**不可重复**） */
  name: string;
  /** 职位（必填） */
  position: string;
  /** 团队id */
  team_id: string;
  /** 成员Id */
  member_record_id: string;
}

/** 更新成员权限 - 请求参数 */
interface UpdateMemberPermRequest {
  /** 团队id */
  team_id: string;
  /** 成员Id */
  member_record_id: string;
  /** 权限列表（必填） */
  perms: string[];
}

/** 更新成员积分 - 请求参数 */
interface UpdateMemberPointRequest {
  /** 团队id */
  team_id: string;
  /** 成员Id */
  member_record_id: string;
  /**
   * 积分分配方式（必填）
   * - 1: 共享团队积分
   * - 2: 设置积分上限
   */
  point_share_type: number;
  /** 积分上限（仅 point_share_type=2 时必填，不能低于已用积分） */
  limit?: number;
  /** 成员是否可见团队积分余额（1: 可见, 0: 不可见） */
  point_balance_visible?: number;
}

/** 停用成员 - 请求参数 */
interface ToggleMemberStatusRequest {
  /** 成员记录Id */
  member_record_id: string;
}

/**
 * 团队下某成员创作记录列表 - 请求参数
 */
interface GetTeamMemberRecordListRequest {
  /** 团队ID */
  team_id: string;
  /** 成员记录ID */
  member_record_id: string;
  /** 开始日期*/
  start_date: string;
  /** 结束日期 */
  end_date: string;
  /** 创作方式 */
  creative_method?: number | string;
  /** 模型列表 */
  model_id?: string;
  /** 页码（默认1） */
  page: number;
  /** 每页数量（默认20） */
  page_size?: number;
}

/** 团队积分变更列表 - 请求参数 */
export interface GetTeamPointChangeRequest {
  /** 团队ID */
  team_id: string;
  /** 开始日期 */
  start_date: string;
  /** 结束日期 */
  end_date: string;
  /** 积分变更类型 */
  type: number | string;
  /** 搜索关键词（可选） */
  keyword?: string;
  /** 页码（默认1） */
  page?: number;
  /** 每页数量（默认20） */
  page_size?: number;
}

/** 团队积分变更列表 - 响应数据 */
export interface TeamPointListItem {
  /** 积分变更ID */
  id: string;
  /** 积分变更类型: 1-获赠积分，2-消耗积分，3-会员积分，4-购买积分，5-过期积分，6-积分转入，7-积分转出 */
  type: number | string;
  /** 积分变更时间 */
  time: string;
  /** 积分变动数量 */
  points: string;
  /** 操作人 */
  operator: string;
  /** 操作场景 */
  creative_method: number;
  /** 模型ID */
  model_id: string;
  /** 模型名称 */
  model_name: string;
  /** 对应节点的团队余额 */
  balance_points: string;
  /** 操作场景名称 */
  title: string;
}

/** 调整团队消耗积分上限 - 请求参数 */
export interface AdjustTeamPointsCapRequest {
  /** 团队ID */
  team_id: string;
  /** 积分上限 不得低于本月已用积分 */
  limit?: number | string;
}

/** 积分预警提醒 - 请求参数 */
export interface PointsWarningReminderRequest {
  /** 团队ID */
  team_id: string;
  /** 月度积分消耗预警提醒 */
  monthly_warning_reminder: boolean;
  /** 月度积分消耗预警提醒阈值 */
  monthly_warning_threshold?: number | string;
  /** 团队剩余积分预警提醒 */
  balance_warning_reminder: boolean;
  /** 团队剩余积分预警提醒阈值 */
  balance_warning_threshold?: number | string;
}

interface SwitchTeamRequest {
  /** 团队ID，传空字符串表示切换到个人视图 */
  team_id: string;
}

export const TeamManageApi = {
  /**
   * 创建团队
   *
   * @param {CreateTeamRequest} params - 请求参数
   * @returns {Promise<CreateTeamResponse>}
   *
   */
  createTeam: (params: CreateTeamRequest): Promise<BaseResponse<CreateTeamResponse>> =>
    http.post("/api/v1/team/create", params),

  /**
   * 切换团队
   *
   * @param {SwitchTeamRequest} params - 请求参数
   * @property {string} team_id - 团队ID，传空字符串表示切换到个人视图
   * @returns {Promise<BaseResponse>}
   *
   */
  switchTeam: (params: SwitchTeamRequest): Promise<BaseResponse> => http.post("/api/v1/team/switch", params),

  /**
   * 获取团队详情
   *
   * @param {object} params - 请求参数
   * @returns {Promise<GetTeamDetailResponse>}
   *
   */
  getTeamDetail: (params: { team_id: string }): Promise<BaseResponse<GetTeamDetailResponse>> =>
    http.get(`/api/v1/team/info/detail`, { params }),

  /**
   * 获取团队成员列表
   *
   * @param {GetMemberListRequest} params - 请求参数
   * @returns {Promise<BaseResponse<PaginationResponse<MemberItem>>>}
   */
  getMemberList: (params: GetMemberListRequest): Promise<BaseResponse<PaginationResponse<MemberItem>>> =>
    http.get("/api/v1/team/members", { params }),

  /**
   * 更新团队基础信息
   *
   * @param {UpdateTeamRequest} params - 请求参数
   * @returns {Promise<BaseResponse<GetTeamDetailResponse>>}
   *
   */
  updateTeam: (params: UpdateTeamRequest): Promise<BaseResponse<GetTeamDetailResponse>> =>
    http.post(`/api/v1/team/update/basic`, params),

  /**
   * 解散团队
   *
   * @param {string} team_id - 团队 ID
   * @returns {Promise<BaseResponse>}
   *
   * @warning 解散后数据不可恢复，请谨慎操作
   */
  disbandTeam: (team_id: string): Promise<BaseResponse> => http.post(`/api/v1/team/disband`, { team_id }),

  /**
   * 转让团队
   *
   * @param {string} team_id - 团队 ID
   * @returns {Promise<BaseResponse>}
   *
   */
  transferTeam: (params: TransferTeamRequest): Promise<BaseResponse> => http.post(`/api/v1/team/transfer`, params),

  /**
   * 邀请成员，生成邀请链接
   *
   * @param {InviteMemberRequest} params - 请求参数
   * @returns {Promise<BaseResponse<InviteMemberResponse>>}
   *
   */
  inviteMember: (params: InviteMemberRequest): Promise<BaseResponse<InviteMemberResponse>> =>
    http.post("/api/v1/team/invite", params),

  /**
   * 获取邀请信息
   *
   * @param {string} invite_id - 邀请ID
   * @returns {Promise<BaseResponse>}
   */
  getInviteInformation: (invite_id: string): Promise<BaseResponse> =>
    http.get(`/api/v1/team/invite/information`, { params: { invite_id } }),

  /**
   * 再次邀请时，根据之前填的信息生成邀请Id
   *
   * @property {string} team_id - 团队ID
   * @property {string} member_record_id - 成员记录Id
   * @returns {Promise<BaseResponse<InviteMemberResponse>>}
   */
  redoMemberInvite: (params: RedoMemberInviteRequest): Promise<BaseResponse<InviteMemberResponse>> =>
    http.post("/api/v1/team/invite/redo", params),

  /**
   * 更新成员基本信息
   * @property {string} name - 姓名备注（必填，**不可重复**）
   * @property {string} position - 职位（必填）
   * @property {string} team_id - 团队id
   * @property {string} member_record_id - 成员记录Id
   * @param {UpdateMemberRequest} params - 请求参数
   * @returns {Promise<BaseResponse>}
   */
  updateMemberInfo: (params: UpdateMemberInfoRequest): Promise<BaseResponse> =>
    http.post(`/api/v1/team/member/update/info`, params),

  /**
   * 更新成员权限
   *
   * @param {UpdateMemberPermRequest} params - 请求参数
   * @property {string} team_id - 团队id
   * @property {string} member_record_id - 成员记录Id
   * @property {string[]} perms - 权限列表（必填）
   * @returns {Promise<BaseResponse>}
   *
   */
  updateMemberPerm: (params: UpdateMemberPermRequest): Promise<BaseResponse> =>
    http.post(`/api/v1/team/member/update/perm`, params),

  /**
   * 更新成员积分设置
   *
   * @param {UpdateMemberPointRequest} params - 请求参数
   * @property {string} team_id - 团队id
   * @property {string} member_record_id - 成员记录Id
   * @property {number} point_share_type - 积分分配方式（必填）1: 共享团队积分 2: 设置积分上限
   * @property {number} [limit] - 积分上限（仅 point_share_type=2 时必填，不能低于已用积分）
   * @property {boolean} [point_balance_visible] - 成员是否可见团队积分余额
   * @returns {Promise<BaseResponse>}
   *
   */
  updateMemberPoint: (params: UpdateMemberPointRequest): Promise<BaseResponse> =>
    http.post(`/api/v1/team/member/update/point`, params),

  /**
   * 停用成员
   *
   * @property {string} member_record_id - 成员记录Id
   * @param {ToggleMemberStatusRequest} params - 请求参数
   * @returns {Promise<ToggleMemberStatusResponse>}
   *
   */
  toggleMemberStatus: (params: ToggleMemberStatusRequest) => http.post(`/api/v1/team/member/deactive`, params),

  /**
   * 获取用户已加入团队列表
   * @returns {Promise<BaseResponse<TeamListItemResponse>>}
   */
  getUserTeamList: (params: {
    page: number;
    page_size: number;
  }): Promise<BaseResponse<PaginationResponse<TeamListItemResponse>>> =>
    http.get<BaseResponse<PaginationResponse<TeamListItemResponse>>>("/api/v1/team/joined", { params }),

  /**
   * 团队下某成员创作记录列表
   * @param {GetTeamMemberRecordListRequest} params - 请求参数
   * @property {string} team_id - 团队ID
   * @property {string} member_record_id - 成员ID
   * @property {string} start_date - 开始日期
   * @property {string} end_date - 结束日期
   * @property {number} [creative_method] - 创作方式
   * @property {string} [model_id] - 模型ID
   * @returns {Promise<BaseResponse<PaginationResponse & {cost_points_consumption: number, name: string }>>}
   */
  getTeamMemberRecordList: (params: GetTeamMemberRecordListRequest) =>
    http.get<BaseResponse<PaginationResponse & { cost_points_consumption: number; name: string }>>(
      "/api/v1/team/member/record",
      { params }
    ),

  /**
   * 导出某成员创作记录
   */
  exportTeamMemberRecord: (params: GetTeamMemberRecordListRequest): void => {
    const userStore = useUserStore();
    window.open(
      `${import.meta.env.VITE_API_BASE_URL}/api/v1/team/member/record/export?token=${userStore.token}&team_id=${params.team_id}&member_record_id=${params.member_record_id}&start_date=${params.start_date}&end_date=${params.end_date}&creative_method=${params.creative_method}&model_id=${params.model_id}`,
      "_blank"
    );
  },

  /**
   * 下载某成员某条创作记录中的素材（图片或视频）
   */
  downloadTeamMemberRecord: (params: { record_id: string }): void => {
    const userStore = useUserStore();
    window.open(`${import.meta.env.VITE_API_BASE_URL}/api/v1/team/member/materials/download?token=${userStore.token}&record_id=${params.record_id}`, "_blank");
  },

  /**
   * 团队积分变更列表
   * @param {GetTeamPointChangeRequest} params - 请求参数
   * @property {string} team_id - 团队ID
   * @property {string} start_date - 开始日期
   * @property {string} end_date - 结束日期
   * @property {number} type - 积分变更类型
   * @property {string} keyword - 搜索关键词（可选）
   * @returns {Promise<BaseResponse<PaginationResponse<TeamPointListItem>>>}
   */
  getTeamPointChangeList: (
    params: GetTeamPointChangeRequest
  ): Promise<BaseResponse<PaginationResponse<TeamPointListItem>>> =>
    http.get<BaseResponse<PaginationResponse<TeamPointListItem>>>("/api/v1/team/point/list", { params }),

  /**
   * 导出团队积分变更明细记录
   * @param {GetTeamPointChangeRequest} params - 请求参数
   * @property {string} team_id - 团队ID
   * @property {string} start_date - 开始日期
   * @property {string} end_date - 结束日期
   * @property {number} type - 积分变更类型
   * @property {string} keyword - 搜索关键词（可选）
   * @returns {void}
   */
  exportTeamPointChangeList: (params: GetTeamPointChangeRequest) =>{
    const userStore = useUserStore();
    window.open(`${import.meta.env.VITE_API_BASE_URL}/api/v1/team/point/export?token=${userStore.token}&team_id=${params.team_id}&start_date=${params.start_date}&end_date=${params.end_date}&type=${params.type}&keyword=${params.keyword}`, "_blank");
  },

  /**
   * 调整团队消耗积分上限
   * @param {AdjustTeamPointsCapRequest} params - 请求参数
   * @property {string} team_id - 团队ID
   * @property {number} limit - 积分上限（必填，不能低于已用积分）
   * @returns {Promise<BaseResponse>}
   */
  adjustTeamPointsCap: (params: AdjustTeamPointsCapRequest): Promise<BaseResponse> =>
    http.post(`/api/v1/team/point/limit`, params),

  /**
   * 团队积分预警设置
   * @param {PointsWarningReminderRequest} params - 请求参数
   * @property {string} team_id - 团队ID
   * @property {number} monthly_warning_reminder - 月度预警提醒（必填）
   * @property {number} monthly_warning_threshold - 月度预警额度
   * @property {number} balance_warning_reminder - 余额预警提醒（必填）
   * @property {number} balance_warning_threshold - 余额预警额度
   * @returns {Promise<BaseResponse>}
   */
  savePointsWarningReminder: (params: PointsWarningReminderRequest): Promise<BaseResponse> =>
    http.post(`/api/v1/team/point/warning`, params),
};
